export type WebFrequencyStatus = 'libre' | 'asignada' | 'reservada' | 'bloqueada';

export interface WebFrequency {
  id: number;
  frequency: string;
  numericValue: number;
  assignedRadio: string | null;
  assignedRadioId: number | null;
  status: WebFrequencyStatus;
  reservedFor: string | null;
  reservedAt: string | null;
  expiresAt: string | null;
  assignedAt: string | null;
  blockedAt: string | null;
  blockedReason: string | null;
}

// ─── RADIO NAMES POOL ───
const radioNames = [
  'Radio Ritmo Latino', 'La Voz del Llano', 'Radio Éxitos TV', 'Radio Fiesta Mix',
  'Rock Total Radio', 'Radio Sonora Digital', 'Deportes Total Radio', 'Radio Internacional Voz',
  'Radio Urbana 101', 'Radio Élite Digital', 'Radio Vida Nueva', 'Radio Universo Online',
  'Radio Festival TV', 'Radio Brisas del Mar', 'Radio Clásica FM', 'Radio Folclor Andino',
  'Radio Retro Hits', 'Radio TechBeats', 'Radio Deportiva Total', 'Radio Gospel Internacional',
  'La Kalle 99.9', 'Radio Voz del Pueblo', 'Radio Stereo Visión', 'Radio Nueva Actitud',
  'Noticias 24/7 Radio', 'La Mexicana FM', 'Radio Fe y Esperanza', 'Banda Radio MX',
  'Radio Deep House Nights', 'Radio Jazz Lounge', 'Radio Salsa Brava', 'Radio Reggae Roots',
  'Radio Tropicalísima', 'Radio Cumbia Total', 'Radio Hip Hop Nation', 'Radio Indie Lab',
  'Radio Clásicos 80s', 'Radio Country Road', 'Radio Blues Alley', 'Radio Metal Thunder',
  'Radio World Beats', 'Radio Chill Vibes', 'Radio Dance Floor', 'Radio Funk Masters',
];

function randRadio(exclude: Set<string> = new Set()): { name: string; id: number } {
  const available = radioNames.filter((n) => !exclude.has(n));
  const pick = available[Math.floor(Math.random() * available.length)];
  const idx = radioNames.indexOf(pick);
  return { name: pick, id: idx + 1 };
}

// ─── GENERAR TODAS LAS FRECUENCIAS SECUENCIALES ───
const BAND_START = 87.0;
const BAND_END = 200.0;
const BAND_STEP = 0.01;
const TOTAL_FREQS = Math.floor((BAND_END - BAND_START) / BAND_STEP) + 1;

// Seeded deterministic indices
const SEED = 42;
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const rng = seededRandom(SEED);

function generateFrequencies(): WebFrequency[] {
  const result: WebFrequency[] = [];
  const usedRadios = new Set<string>();

  const assignedIndices = new Set<number>();
  const assignedRadios: Map<number, string> = new Map();
  const assignedDates: Map<number, string> = new Map();

  while (assignedIndices.size < 34) {
    const candidate = Math.floor(rng() * TOTAL_FREQS);
    if (assignedIndices.has(candidate)) continue;
    assignedIndices.add(candidate);
    const r = randRadio(usedRadios);
    usedRadios.add(r.name);
    assignedRadios.set(candidate, r.name);
    const month = String((assignedIndices.size % 5) + 1).padStart(2, '0');
    const day = String((assignedIndices.size % 25) + 1).padStart(2, '0');
    assignedDates.set(candidate, `2026-0${month}-${day}`);
  }

  const reservedIndices = new Set<number>();
  const reservedNames: Map<number, string> = new Map();
  const reservedExpirations: Map<number, string> = new Map();

  const expDates = [
    '2026-06-14T23:59:59', '2026-06-18T12:00:00', '2026-06-22T23:59:59',
    '2026-06-30T23:59:59', '2026-07-05T12:00:00', '2026-07-10T23:59:59',
    '2026-07-15T12:00:00', '2026-06-12T18:00:00', '2026-06-25T23:59:59',
    '2026-06-28T12:00:00',
  ];

  let expIdx = 0;
  while (reservedIndices.size < 10) {
    const candidate = Math.floor(rng() * TOTAL_FREQS);
    if (assignedIndices.has(candidate) || reservedIndices.has(candidate)) continue;
    reservedIndices.add(candidate);
    const r = randRadio(usedRadios);
    usedRadios.add(r.name);
    reservedNames.set(candidate, r.name);
    reservedExpirations.set(candidate, expDates[expIdx % expDates.length]);
    expIdx++;
  }

  const blockedIndices = new Set<number>();
  const blockedReasons = [
    'Incumplimiento de pago',
    'Revisión de licencia',
    'Solicitud de baja',
    'Auditoría administrativa',
    'Fraude detectado',
    'Violación de términos',
    'Reclamación de frecuencia',
    'Orden regulatoria',
    'Inactividad prolongada',
    'Fusión pendiente',
  ];
  const blockedReasonMap: Map<number, string> = new Map();

  while (blockedIndices.size < 10) {
    const candidate = Math.floor(rng() * TOTAL_FREQS);
    if (assignedIndices.has(candidate) || reservedIndices.has(candidate) || blockedIndices.has(candidate)) continue;
    blockedIndices.add(candidate);
    blockedReasonMap.set(candidate, blockedReasons[blockedIndices.size - 1]);
  }

  for (let i = 0; i < TOTAL_FREQS; i++) {
    const numericValue = Math.round((BAND_START + i * BAND_STEP) * 100) / 100;
    let status: WebFrequencyStatus;
    let radio: string | null = null;
    let radioId: number | null = null;
    let reservedFor: string | null = null;
    let reservedAt: string | null = null;
    let expiresAt: string | null = null;
    let assignedAt: string | null = null;
    let blockedAt: string | null = null;
    let blockedReason: string | null = null;

    if (blockedIndices.has(i)) {
      status = 'bloqueada';
      blockedAt = '2026-06-01';
      blockedReason = blockedReasonMap.get(i) || null;
    } else if (assignedIndices.has(i)) {
      status = 'asignada';
      radio = assignedRadios.get(i)!;
      radioId = radioNames.indexOf(radio) + 1;
      assignedAt = assignedDates.get(i) || '2026-01-15';
    } else if (reservedIndices.has(i)) {
      status = 'reservada';
      reservedFor = reservedNames.get(i)!;
      reservedAt = '2026-05-20';
      expiresAt = reservedExpirations.get(i) || '2026-07-01T23:59:59';
    } else {
      status = 'libre';
    }

    result.push({
      id: i + 1,
      frequency: `${numericValue.toFixed(2)} RFW`,
      numericValue,
      assignedRadio: radio,
      assignedRadioId: radioId,
      status,
      reservedFor,
      reservedAt,
      expiresAt,
      assignedAt,
      blockedAt,
      blockedReason,
    });
  }

  return result;
}

export const saFrequenciesData: WebFrequency[] = generateFrequencies();

// ─── SOLICITUDES DE FRECUENCIA PREFERIDA ───
export type FrequencyRequestStatus =
  | 'requested'
  | 'pending_payment'
  | 'paid'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'assigned'
  | 'expired'
  | 'cancelled';

export interface FrequencyRequest {
  id: number;
  radioName: string;
  radioId: number;
  owner: string;
  requestedFrequency: string;
  numericValue: number;
  currentFrequency: string;
  plan: string;
  country: string;
  responsible: string;
  status: FrequencyRequestStatus;
  paymentStatus: 'unpaid' | 'paid' | 'refunded';
  requestedAt: string;
  expiresAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;
  invoiceGeneratedAt: string | null;
  invoiceId: string | null;
  paidAt: string | null;
  assignedAt: string | null;
  price: number;
  currency: string;
  tax: number;
  additionalCharge: number;
  totalPrice: number;
  notes: string | null;
  history: { timestamp: string; action: string; detail: string; performedBy: string }[];
}

export const saFrequencyRequests: FrequencyRequest[] = [
  {
    id: 1,
    radioName: 'Radio Éxitos TV',
    radioId: 3,
    owner: 'Carlos Méndez',
    requestedFrequency: '94.07 RFW',
    numericValue: 94.07,
    currentFrequency: '88.35 RFW',
    plan: 'Pro',
    country: 'México',
    responsible: 'carlos.lopez@rfw.com',
    status: 'requested',
    paymentStatus: 'unpaid',
    requestedAt: '2026-07-10T08:15:00',
    expiresAt: '2026-08-10T08:15:00',
    approvedAt: null,
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: null,
    invoiceId: null,
    paidAt: null,
    assignedAt: null,
    price: 49,
    currency: 'USD',
    tax: 0,
    additionalCharge: 0,
    totalPrice: 49,
    notes: 'Cobertura en zona rural.',
    history: [
      { timestamp: '2026-07-10T08:15:00', action: 'requested', detail: 'Solicitud creada por el sistema.', performedBy: 'sistema@rfw.com' },
    ],
  },
  {
    id: 2,
    radioName: 'La Voz del Llano',
    radioId: 2,
    owner: 'María Fernández',
    requestedFrequency: '99.99 RFW',
    numericValue: 99.99,
    currentFrequency: '92.15 RFW',
    plan: 'Premium',
    country: 'Colombia',
    responsible: 'maria.gomez@rfw.com',
    status: 'pending_payment',
    paymentStatus: 'unpaid',
    requestedAt: '2026-07-08T14:30:00',
    expiresAt: '2026-08-08T14:30:00',
    approvedAt: null,
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: '2026-07-08T15:00:00',
    invoiceId: 'INV-PF-2026-0052',
    paidAt: null,
    assignedAt: null,
    price: 79,
    currency: 'USD',
    tax: 12.64,
    additionalCharge: 0,
    totalPrice: 91.64,
    notes: 'Frecuencia estratégica mercado Bogotá.',
    history: [
      { timestamp: '2026-07-08T14:30:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-08T15:00:00', action: 'invoice_generated', detail: 'Invoice INV-PF-2026-0052 generada por $91.64 USD.', performedBy: 'carlos.lopez@rfw.com' },
    ],
  },
  {
    id: 3,
    radioName: 'Rock Total Radio',
    radioId: 5,
    owner: 'Pedro Ramírez',
    requestedFrequency: '101.10 RFW',
    numericValue: 101.10,
    currentFrequency: '95.70 RFW',
    plan: 'Business',
    country: 'Argentina',
    responsible: 'carlos.lopez@rfw.com',
    status: 'paid',
    paymentStatus: 'paid',
    requestedAt: '2026-07-05T10:00:00',
    expiresAt: '2026-08-05T10:00:00',
    approvedAt: null,
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: '2026-07-05T10:30:00',
    invoiceId: 'INV-PF-2026-0048',
    paidAt: '2026-07-06T09:15:00',
    assignedAt: null,
    price: 99,
    currency: 'USD',
    tax: 15.84,
    additionalCharge: 0,
    totalPrice: 114.84,
    notes: null,
    history: [
      { timestamp: '2026-07-05T10:00:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-05T10:30:00', action: 'invoice_generated', detail: 'Invoice INV-PF-2026-0048 generada por $114.84 USD.', performedBy: 'carlos.lopez@rfw.com' },
      { timestamp: '2026-07-06T09:15:00', action: 'paid', detail: 'Pago confirmado por $114.84 USD.', performedBy: 'sistema@rfw.com' },
    ],
  },
  {
    id: 4,
    radioName: 'Radio Sonora Digital',
    radioId: 6,
    owner: 'Ana López',
    requestedFrequency: '94.70 RFW',
    numericValue: 94.70,
    currentFrequency: '105.22 RFW',
    plan: 'Pro',
    country: 'México',
    responsible: 'maria.gomez@rfw.com',
    status: 'under_review',
    paymentStatus: 'paid',
    requestedAt: '2026-07-03T16:45:00',
    expiresAt: '2026-08-03T16:45:00',
    approvedAt: null,
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: '2026-07-03T17:00:00',
    invoiceId: 'INV-PF-2026-0045',
    paidAt: '2026-07-04T11:20:00',
    assignedAt: null,
    price: 49,
    currency: 'USD',
    tax: 7.84,
    additionalCharge: 10,
    totalPrice: 66.84,
    notes: 'Migración por interferencia detectada.',
    history: [
      { timestamp: '2026-07-03T16:45:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-03T17:00:00', action: 'invoice_generated', detail: 'Invoice INV-PF-2026-0045 generada por $66.84 USD.', performedBy: 'carlos.lopez@rfw.com' },
      { timestamp: '2026-07-04T11:20:00', action: 'paid', detail: 'Pago confirmado por $66.84 USD.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-05T08:00:00', action: 'under_review', detail: 'En revisión por Super Admin.', performedBy: 'maria.gomez@rfw.com' },
    ],
  },
  {
    id: 5,
    radioName: 'Radio Fiesta Mix',
    radioId: 4,
    owner: 'Luis Torres',
    requestedFrequency: '100.00 RFW',
    numericValue: 100.00,
    currentFrequency: '89.90 RFW',
    plan: 'Premium',
    country: 'España',
    responsible: 'carlos.lopez@rfw.com',
    status: 'approved',
    paymentStatus: 'paid',
    requestedAt: '2026-07-01T09:00:00',
    expiresAt: '2026-08-01T09:00:00',
    approvedAt: '2026-07-02T08:00:00',
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: '2026-07-01T09:30:00',
    invoiceId: 'INV-PF-2026-0040',
    paidAt: '2026-07-01T14:22:00',
    assignedAt: null,
    price: 79,
    currency: 'USD',
    tax: 12.64,
    additionalCharge: 0,
    totalPrice: 91.64,
    notes: null,
    history: [
      { timestamp: '2026-07-01T09:00:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-01T09:30:00', action: 'invoice_generated', detail: 'Invoice INV-PF-2026-0040 generada por $91.64 USD.', performedBy: 'carlos.lopez@rfw.com' },
      { timestamp: '2026-07-01T14:22:00', action: 'paid', detail: 'Pago confirmado por $91.64 USD.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-02T08:00:00', action: 'approved', detail: 'Aprobada por Super Admin.', performedBy: 'carlos.lopez@rfw.com' },
    ],
  },
  {
    id: 6,
    radioName: 'Radio Urbana 101',
    radioId: 9,
    owner: 'Javier Herrera',
    requestedFrequency: '103.50 RFW',
    numericValue: 103.50,
    currentFrequency: '91.45 RFW',
    plan: 'Business',
    country: 'Colombia',
    responsible: 'maria.gomez@rfw.com',
    status: 'assigned',
    paymentStatus: 'paid',
    requestedAt: '2026-06-20T11:00:00',
    expiresAt: null,
    approvedAt: '2026-06-21T09:30:00',
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: '2026-06-20T11:30:00',
    invoiceId: 'INV-PF-2026-0032',
    paidAt: '2026-06-22T10:15:00',
    assignedAt: '2026-06-22T10:30:00',
    price: 99,
    currency: 'USD',
    tax: 15.84,
    additionalCharge: 0,
    totalPrice: 114.84,
    notes: null,
    history: [
      { timestamp: '2026-06-20T11:00:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-06-20T11:30:00', action: 'invoice_generated', detail: 'Invoice INV-PF-2026-0032 generada por $114.84 USD.', performedBy: 'carlos.lopez@rfw.com' },
      { timestamp: '2026-06-22T10:15:00', action: 'paid', detail: 'Pago confirmado por $114.84 USD.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-06-21T09:30:00', action: 'approved', detail: 'Aprobada por Super Admin.', performedBy: 'carlos.lopez@rfw.com' },
      { timestamp: '2026-06-22T10:30:00', action: 'assigned', detail: 'Frecuencia 103.50 RFW asignada a Radio Urbana 101.', performedBy: 'sistema@rfw.com' },
    ],
  },
  {
    id: 7,
    radioName: 'Radio Internacional Voz',
    radioId: 8,
    owner: 'Diana Castro',
    requestedFrequency: '107.90 RFW',
    numericValue: 107.90,
    currentFrequency: '96.80 RFW',
    plan: 'Pro',
    country: 'Chile',
    responsible: 'maria.gomez@rfw.com',
    status: 'rejected',
    paymentStatus: 'unpaid',
    requestedAt: '2026-07-02T07:30:00',
    expiresAt: null,
    approvedAt: null,
    rejectedAt: '2026-07-03T14:00:00',
    rejectedReason: 'Frecuencia ya está reservada para otra radio con prioridad.',
    invoiceGeneratedAt: null,
    invoiceId: null,
    paidAt: null,
    assignedAt: null,
    price: 49,
    currency: 'USD',
    tax: 0,
    additionalCharge: 0,
    totalPrice: 49,
    notes: 'Frecuencia ya está reservada.',
    history: [
      { timestamp: '2026-07-02T07:30:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-03T14:00:00', action: 'rejected', detail: 'Rechazada: Frecuencia ya está reservada para otra radio con prioridad.', performedBy: 'maria.gomez@rfw.com' },
    ],
  },
  {
    id: 8,
    radioName: 'Deportes Total Radio',
    radioId: 10,
    owner: 'Roberto Sánchez',
    requestedFrequency: '120.00 RFW',
    numericValue: 120.00,
    currentFrequency: '118.50 RFW',
    plan: 'Business',
    country: 'Argentina',
    responsible: 'carlos.lopez@rfw.com',
    status: 'expired',
    paymentStatus: 'unpaid',
    requestedAt: '2026-06-01T09:00:00',
    expiresAt: '2026-07-01T09:00:00',
    approvedAt: null,
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: null,
    invoiceId: null,
    paidAt: null,
    assignedAt: null,
    price: 99,
    currency: 'USD',
    tax: 15.84,
    additionalCharge: 0,
    totalPrice: 114.84,
    notes: 'Sin pago dentro del plazo.',
    history: [
      { timestamp: '2026-06-01T09:00:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-07-01T09:00:00', action: 'expired', detail: 'Solicitud expirada por falta de pago en el plazo establecido.', performedBy: 'sistema@rfw.com' },
    ],
  },
  {
    id: 9,
    radioName: 'Radio Élite Digital',
    radioId: 11,
    owner: 'Sofía Vargas',
    requestedFrequency: '91.30 RFW',
    numericValue: 91.30,
    currentFrequency: '90.10 RFW',
    plan: 'Pro',
    country: 'Perú',
    responsible: 'maria.gomez@rfw.com',
    status: 'cancelled',
    paymentStatus: 'unpaid',
    requestedAt: '2026-06-25T12:00:00',
    expiresAt: null,
    approvedAt: null,
    rejectedAt: null,
    rejectedReason: null,
    invoiceGeneratedAt: null,
    invoiceId: null,
    paidAt: null,
    assignedAt: null,
    price: 49,
    currency: 'USD',
    tax: 0,
    additionalCharge: 0,
    totalPrice: 49,
    notes: 'Radio decidió no continuar con la solicitud.',
    history: [
      { timestamp: '2026-06-25T12:00:00', action: 'requested', detail: 'Solicitud creada.', performedBy: 'sistema@rfw.com' },
      { timestamp: '2026-06-28T10:00:00', action: 'cancelled', detail: 'Cancelada por decisión de la radio.', performedBy: 'carlos.lopez@rfw.com' },
    ],
  },
];

// ─── RADIOS DISPONIBLES PARA ASIGNACIÓN ───
export const saAvailableRadiosForAssignment = radioNames.map((name, idx) => ({
  id: idx + 1,
  name,
}));

// ─── FRECUENCIA PREFERIDA CONFIG ───
export interface PreferredFrequencyConfig {
  enabled: boolean;
  basePrice: number;
  currency: string;
  taxRate: number;
  plansPricing: { plan: string; price: number; additionalCharge: number }[];
  countriesPricing: { country: string; price: number; additionalCharge: number }[];
  promotionalPrice: number | null;
  promotionalStartDate: string | null;
  promotionalEndDate: string | null;
}

export const frequencyPreferredConfig: PreferredFrequencyConfig = {
  enabled: true,
  basePrice: 49,
  currency: 'USD',
  taxRate: 16,
  plansPricing: [
    { plan: 'Free', price: 0, additionalCharge: 0 },
    { plan: 'Básico', price: 29, additionalCharge: 0 },
    { plan: 'Pro', price: 49, additionalCharge: 0 },
    { plan: 'Premium', price: 79, additionalCharge: 0 },
    { plan: 'Business', price: 99, additionalCharge: 0 },
    { plan: 'Empresarial', price: 149, additionalCharge: 0 },
    { plan: 'Oro', price: 199, additionalCharge: 0 },
    { plan: 'Regional', price: 249, additionalCharge: 0 },
    { plan: 'Nacional', price: 299, additionalCharge: 0 },
    { plan: 'Ilimitado', price: 499, additionalCharge: 0 },
  ],
  countriesPricing: [
    { country: 'México', price: 0, additionalCharge: 0 },
    { country: 'Colombia', price: 0, additionalCharge: 5 },
    { country: 'Argentina', price: 0, additionalCharge: 10 },
    { country: 'Chile', price: 0, additionalCharge: 8 },
    { country: 'Perú', price: 0, additionalCharge: 6 },
    { country: 'España', price: 0, additionalCharge: 12 },
    { country: 'Estados Unidos', price: 0, additionalCharge: 15 },
  ],
  promotionalPrice: 29.99,
  promotionalStartDate: '2026-07-01',
  promotionalEndDate: '2026-07-31',
};

// ─── FREQUENCY HISTORY ───
export type FrequencyHistoryAction =
  | 'asignacion'
  | 'liberacion'
  | 'reserva'
  | 'cancelacion_reserva'
  | 'transferencia'
  | 'bloquear'
  | 'reactivar'
  | 'solicitud_preferida'
  | 'activacion_preferida';

export interface FrequencyHistoryEvent {
  id: number;
  frequencyId: number;
  action: FrequencyHistoryAction;
  detail: string;
  radioName: string | null;
  timestamp: string;
  performedBy: string;
}

function generateFrequencyHistory(): FrequencyHistoryEvent[] {
  const history: FrequencyHistoryEvent[] = [];
  let hId = 1;
  const freqs = saFrequenciesData;

  const assignedFreqs = freqs.filter((f) => f.status === 'asignada' && f.assignedRadio);
  const admins = ['carlos.lopez@rfw.com', 'maria.gomez@rfw.com', 'sistema@rfw.com'];

  for (const freq of assignedFreqs.slice(0, 14)) {
    const day = String(Math.floor(rng() * 8) + 1).padStart(2, '0');
    const monthNum = Math.floor(rng() * 5) + 1;
    const month = String(monthNum).padStart(2, '0');
    history.push({
      id: hId++,
      frequencyId: freq.id,
      action: 'asignacion',
      detail: `Frecuencia ${freq.frequency} asignada a ${freq.assignedRadio}`,
      radioName: freq.assignedRadio,
      timestamp: `2026-0${month}-${day} 1${String(Math.floor(rng() * 12) + 1).padStart(2, '0')}:${String(Math.floor(rng() * 60)).padStart(2, '0')}`,
      performedBy: admins[Math.floor(rng() * 3)],
    });
  }

  const reservedFreqs = freqs.filter((f) => f.status === 'reservada' && f.reservedFor);
  for (const freq of reservedFreqs.slice(0, 5)) {
    history.push({
      id: hId++,
      frequencyId: freq.id,
      action: 'reserva',
      detail: `Frecuencia ${freq.frequency} reservada para ${freq.reservedFor}`,
      radioName: freq.reservedFor,
      timestamp: `2026-05-${String(Math.floor(rng() * 28) + 1).padStart(2, '0')} 0${Math.floor(rng() * 9) + 1}:${String(Math.floor(rng() * 60)).padStart(2, '0')}`,
      performedBy: 'carlos.lopez@rfw.com',
    });
  }

  const blockedFreqs = freqs.filter((f) => f.status === 'bloqueada');
  for (let i = 0; i < Math.min(blockedFreqs.length, 4); i++) {
    history.push({
      id: hId++,
      frequencyId: blockedFreqs[i].id,
      action: 'bloquear',
      detail: blockedFreqs[i].blockedReason || 'Frecuencia bloqueada',
      radioName: null,
      timestamp: `2026-06-0${i + 1} 14:${String(30 + i * 5).padStart(2, '0')}`,
      performedBy: 'carlos.lopez@rfw.com',
    });
  }

  history.push({
    id: hId++,
    frequencyId: 9234,
    action: 'cancelacion_reserva',
    detail: 'Reserva de frecuencia cancelada por inactividad',
    radioName: null,
    timestamp: '2026-06-07 08:20',
    performedBy: 'sistema@rfw.com',
  });

  const transferCandidates = assignedFreqs.slice(0, 2);
  if (transferCandidates.length >= 2) {
    history.push({
      id: hId++,
      frequencyId: transferCandidates[0].id,
      action: 'transferencia',
      detail: `Frecuencia ${transferCandidates[0].frequency} transferida a Radio Brisas del Mar`,
      radioName: 'Radio Brisas del Mar',
      timestamp: '2026-06-01 09:15',
      performedBy: 'carlos.lopez@rfw.com',
    });
  }

  // Reactivate
  const freeFreqs = freqs.filter((f) => f.status === 'libre');
  if (freeFreqs.length >= 2) {
    history.push({
      id: hId++,
      frequencyId: freeFreqs[0].id,
      action: 'reactivar',
      detail: 'Frecuencia reactivada después de resolver incumplimiento',
      radioName: null,
      timestamp: '2026-06-10 09:00',
      performedBy: 'maria.gomez@rfw.com',
    });
    history.push({
      id: hId++,
      frequencyId: freeFreqs[1].id,
      action: 'reactivar',
      detail: 'Frecuencia reactivada por baja voluntaria',
      radioName: null,
      timestamp: '2026-06-12 18:00',
      performedBy: 'carlos.lopez@rfw.com',
    });
  }

  return history;
}

export const saFrequencyHistory: FrequencyHistoryEvent[] = generateFrequencyHistory();

export function getFrequencyHistory(frequencyId: number): FrequencyHistoryEvent[] {
  return saFrequencyHistory
    .filter((h) => h.frequencyId === frequencyId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

export function getTimeRemaining(expiresAt: string): { text: string; urgent: boolean; expired: boolean } {
  const now = new Date();
  const exp = new Date(expiresAt);
  const diff = exp.getTime() - now.getTime();

  if (diff <= 0) return { text: 'Expirada', urgent: true, expired: true };

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 30) {
    const months = Math.floor(days / 30);
    return { text: `${months}M`, urgent: false, expired: false };
  }
  if (days > 0) {
    return { text: `${days}d ${hours}h`, urgent: days <= 2, expired: false };
  }
  if (hours > 0) {
    return { text: `${hours}h`, urgent: true, expired: false };
  }
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { text: `${mins}m`, urgent: true, expired: false };
}